///
/// EarthBrowser
///
/// Created by Matt Giger on 11/19/2024
/// Copyright (c) 2024 Geodata Labs
///

import { type geographic } from "@/earthbrowser/utils/geographic"
import { trig } from "@stovokor/math/math"
import { ElevationLoader } from "@/earthbrowser/utils/loaders/ElevationLoader"

const cElevBase		= "https://tiles.earthbrowser.com/elevation/elevbase.webp"

/**
 * BaseElevationLayer provides a medium-resolution global elevation layer (4096x2048)
 * with bicubic interpolation for smooth elevation queries
 *
 * Additionally encodes tile availability information using negative values:
 * - Positive values (1-32767): Elevation data in meters
 * - Zero (0): Ocean or no data
 * - Negative values: Tile availability bitmask for zoom levels 4-12
 */
export class BaseElevationLayer {

	public destroy(): void {
		this.data = undefined
		this.loaded = false
		this.width = 0
		this.height = 0
	}

	public width: number = 0
	public height: number = 0
	public data?: Uint16Array
	public loaded: boolean = false
	public static readonly MIN_ZOOM = 4
	public static readonly MAX_ZOOM = 12

	/**
	 * Load the base elevation layer from a PNG file
	 */
	async load(): Promise<boolean> {
		const loader = new ElevationLoader(cElevBase)
		const imageData = await loader.loadData()
		if(!imageData) {
			return false
		}

		this.data = imageData
		this.loaded = true
		this.width = loader.width
		this.height = loader.height
		return true
	}

	/**
	 * Get elevation at a specific latitude/longitude using nearest neighbor
	 */
	getElevationNearest(geo: geographic): number {
		if (!this.loaded || !this.data) {
			return 0
		}

		// Convert lat/lon to texture coordinates
		// Longitude: -180 to 180 -> 0 to width
		// Latitude: 90 to -90 -> 0 to height
		const x = Math.floor(((geo.longitude + trig.pi) / trig.two_pi) * this.width) % this.width
		const y = Math.floor(((trig.half_pi - geo.latitude) / trig.pi) * this.height) % this.height

		const index = y * this.width + x
		return this.data[index]
	}

	/**
	 * Get elevation at a specific latitude/longitude using bilinear interpolation
	 */
	getElevationBilinear(geo: geographic): number {
		if (!this.loaded || !this.data) {
			return 0
		}

		// Convert lat/lon to continuous texture coordinates
		const fx = ((geo.longitude + trig.pi) / trig.two_pi) * this.width
		const fy = ((trig.half_pi - geo.latitude) / trig.pi) * this.height

		// Get integer coordinates and fractional parts
		const x0 = Math.floor(fx) % this.width
		const y0 = Math.floor(fy) % this.height
		const x1 = (x0 + 1) % this.width
		const y1 = (y0 + 1) % this.height

		const dx = fx - Math.floor(fx)
		const dy = fy - Math.floor(fy)

		// Sample four corners
		const v00 = this.sample(x0, y0)
		const v10 = this.sample(x1, y0)
		const v01 = this.sample(x0, y1)
		const v11 = this.sample(x1, y1)

		// Bilinear interpolation
		const v0 = v00 * (1 - dx) + v10 * dx
		const v1 = v01 * (1 - dx) + v11 * dx
		return v0 * (1 - dy) + v1 * dy
	}

	/**
	 * Get elevation at a specific latitude/longitude using bicubic interpolation
	 */
	getElevation(geo: geographic): number {
		if (!this.loaded || !this.data) {
			return 0
		}

		// Convert lat/lon to continuous texture coordinates
		const fx = ((geo.longitude + trig.pi) / trig.two_pi) * this.width
		const fy = ((trig.half_pi - geo.latitude) / trig.pi) * this.height

		// Get integer coordinates and fractional parts
		const x = Math.floor(fx)
		const y = Math.floor(fy)
		const dx = fx - x
		const dy = fy - y

		// Sample 4x4 grid around the point
		const samples: number[] = []
		for (let j = -1; j <= 2; j++) {
			for (let i = -1; i <= 2; i++) {
				const sx = (x + i + this.width) % this.width
				const sy = (y + j + this.height) % this.height
				samples.push(this.sample(sx, sy))
			}
		}

		// Perform bicubic interpolation
		return this.bicubicInterpolate(samples, dx, dy)
	}

	/**
	 * Sample elevation data at integer coordinates
	 */
	private sample(x: number, y: number): number {
		if (!this.data) return 0

		const index = y * this.width + x
		const value = this.data[index]

		// Handle invalid elevation values (typically > 32768 or 0)
		if (value >= 32768) return 0
		return value
	}

	/**
	 * Cubic interpolation helper function
	 */
	private cubicInterpolate(p: number[], x: number): number {
		return p[1] + 0.5 * x * (
			p[2] - p[0] + x * (
				2.0 * p[0] - 5.0 * p[1] + 4.0 * p[2] - p[3] + x * (
					3.0 * (p[1] - p[2]) + p[3] - p[0]
				)
			)
		)
	}

	/**
	 * Bicubic interpolation using a 4x4 grid of samples
	 * samples: 16 values arranged as rows [y-1], [y], [y+1], [y+2]
	 * dx, dy: fractional coordinates within the center cell [0, 1]
	 */
	private bicubicInterpolate(samples: number[], dx: number, dy: number): number {
		// Interpolate along x for each of the 4 rows
		const arr: number[] = []
		for (let j = 0; j < 4; j++) {
			const row = samples.slice(j * 4, (j + 1) * 4)
			arr.push(this.cubicInterpolate(row, dx))
		}

		// Interpolate along y using the 4 interpolated values
		return this.cubicInterpolate(arr, dy)
	}

	/**
	 * Check if the layer is loaded
	 */
	isLoaded(): boolean {
		return this.loaded
	}

	/**
	 * Get the dimensions of the elevation layer
	 */
	getDimensions(): { width: number; height: number } {
		return { width: this.width, height: this.height }
	}

	/**
	 * Get raw elevation value at pixel coordinates (for debugging)
	 */
	getValueAt(x: number, y: number): number {
		if (!this.data || x < 0 || x >= this.width || y < 0 || y >= this.height) {
			return 0
		}
		return this.data[y * this.width + x]
	}

	/**
	 * Check if elevation tiles are available at a specific location and zoom level
	 * @param geo Geographic coordinate
	 * @param zoom Zoom level to check (4-12)
	 * @returns true if tiles are available at this zoom level
	 */
	hasTileAvailable(x: number, y: number, zoom: number): boolean {
		if (!this.loaded || !this.data) {
			return false
		}

		if (zoom < BaseElevationLayer.MIN_ZOOM || zoom > BaseElevationLayer.MAX_ZOOM) {
			return false
		}

		// Get the pixel coordinates in the base elevation layer
		const index = y * this.width + x
		const value = this.data[index]

		// Negative values encode availability information
		if (value >= 32768) {
			// Convert from unsigned to signed
			const signed = value - 65536
			const mask = -signed
			const bit = 1 << (zoom - BaseElevationLayer.MIN_ZOOM)
			return (mask & bit) !== 0
		}

		return false
	}

	/**
	 * Get the maximum available zoom level for tiles at a specific location
	 * @param geo Geographic coordinate
	 * @returns Maximum zoom level available, or -1 if no data
	 */
	getMaxZoomLevel(geo: geographic): number {
		if (!this.loaded || !this.data) {
			return -1
		}

		const fx = ((geo.longitude + trig.pi) / trig.two_pi) * this.width
		const fy = ((trig.half_pi - geo.latitude) / trig.pi) * this.height
		const x = Math.floor(fx) % this.width
		const y = Math.floor(fy) % this.height

		const index = y * this.width + x
		const value = this.data[index]

		if (value >= 32768) {
			const signed = value - 65536
			const mask = -signed

			// Find the highest set bit
			for (let z = BaseElevationLayer.MAX_ZOOM; z >= BaseElevationLayer.MIN_ZOOM; z--) {
				const bit = 1 << (z - BaseElevationLayer.MIN_ZOOM)
				if (mask & bit) {
					return z
				}
			}
		}

		return -1
	}

	/**
	 * Get all available zoom levels for tiles at a specific location
	 * @param geo Geographic coordinate
	 * @returns Array of available zoom levels
	 */
	getAvailableZoomLevels(geo: geographic): number[] {
		if (!this.loaded || !this.data) {
			return []
		}

		const fx = ((geo.longitude + trig.pi) / trig.two_pi) * this.width
		const fy = ((trig.half_pi - geo.latitude) / trig.pi) * this.height
		const x = Math.floor(fx) % this.width
		const y = Math.floor(fy) % this.height

		const index = y * this.width + x
		const value = this.data[index]

		if (value >= 32768) {
			const signed = value - 65536
			const mask = -signed
			const levels: number[] = []

			for (let z = BaseElevationLayer.MIN_ZOOM; z <= BaseElevationLayer.MAX_ZOOM; z++) {
				const bit = 1 << (z - BaseElevationLayer.MIN_ZOOM)
				if (mask & bit) {
					levels.push(z)
				}
			}

			return levels
		}

		return []
	}

	/**
	 * Get tile coordinates for a given location and zoom level
	 * @param geo Geographic coordinate
	 * @param zoom Zoom level
	 * @returns Tile coordinates {z, y, x}
	 */
	getTileCoordinates(geo: geographic, zoom: number): { z: number; y: number; x: number } {
		const rows = Math.pow(2, zoom)
		const cols = Math.pow(2, zoom + 1)

		const x = Math.floor(((geo.longitude + trig.pi) / trig.two_pi) * cols)
		const y = Math.floor(((trig.half_pi - geo.latitude) / trig.pi) * rows)

		return {
			z: zoom,
			y: Math.max(0, Math.min(y, rows - 1)),
			x: Math.max(0, Math.min(x, cols - 1))
		}
	}
}
